import { Outlet } from "react-router-dom";
import { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import UpdateBanner from "./UpdateBanner";
import WhatsNewModal from "./WhatsNewModal";
import NotificationToasts from "./NotificationToasts";
import { NotificationProvider } from "../contexts/NotificationContext";
import { hayNovedadesSinVer, marcarNovedadesVistas } from "../data/changelog";
import { useAuth } from "../../features/auth/hooks/useAuth";

import "../styles/header.css";
import "../styles/sidebar.css";
import "../styles/main.css";

const MOBILE_BP = 768;
const TABLET_BP = 1024;
const SESSION_MS = 12 * 60 * 60 * 1000; // 12 hours

export default function Layout() {
  const { logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);

  // Al iniciar sesión: si hay una versión del changelog que el usuario no
  // vio, mostrarla una vez automáticamente ("pestaña" de novedades al entrar).
  useEffect(() => {
    if (hayNovedadesSinVer(localStorage.getItem("rol"))) setShowChangelog(true);
  }, []);

  function closeChangelog() {
    marcarNovedadesVistas();
    setShowChangelog(false);
  }

  const toggleSidebar = () => {
    if (window.innerWidth <= MOBILE_BP) {
      setIsMobileOpen(prev => !prev);
    } else {
      const next = !isCollapsed;
      if (next && window.closeAllSubMenus) window.closeAllSubMenus();
      setIsCollapsed(next);
    }
  };

  useEffect(() => {
    const adjustDashboardHeight = () => {
      const header = document.querySelector("nav");
      const dashboard = document.querySelector(".contentDashboard");

      if (!header || !dashboard) return;

      const headerHeight = header.offsetHeight;
      const availableHeight = window.innerHeight - headerHeight;

      dashboard.style.minHeight = `${availableHeight}px`;

      const main = dashboard.querySelector("main");
      const aside = dashboard.querySelector("aside");

      if (!main || !aside) return;
      main.style.minHeight = `${availableHeight}px`;
      aside.style.minHeight = `${availableHeight}px`;
    };

    // run when mounted and when window resizes
    adjustDashboardHeight();
    window.addEventListener("resize", adjustDashboardHeight);

    return () => window.removeEventListener("resize", adjustDashboardHeight);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;

      if (window.closeAllSubMenus) window.closeAllSubMenus();

      if (w <= MOBILE_BP) {
        // Phone / small tablet → overlay mode; reset collapsed
        setIsCollapsed(false);
      } else {
        // Desktop / large tablet → close overlay
        setIsMobileOpen(false);
        // Auto-collapse at tablet range (769–1024 px)
        if (w <= TABLET_BP) {
          setIsCollapsed(true);
        }
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Session timeout: auto-logout after 12 h ──────────────────────────────
  useEffect(() => {
    function checkExpiry() {
      const loginAt = parseInt(localStorage.getItem("login_at") || "0", 10);
      if (loginAt && Date.now() - loginAt > SESSION_MS) {
        logout();
      }
    }
    checkExpiry();
    const timer = setInterval(checkExpiry, 60_000);
    document.addEventListener("visibilitychange", checkExpiry);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", checkExpiry);
    };
  }, [logout]);

  return (
    <NotificationProvider>
      <Header toggleSidebar={toggleSidebar} onOpenChangelog={() => setShowChangelog(true)} />
      <div
        className={`contentDashboard ${isCollapsed ? "collapsed" : ""} ${
          isMobileOpen ? "mobile-open" : ""
        }`}
      >
        <div
          className="mobile-overlay"
          id="mobileOverlay"
          aria-hidden={!isMobileOpen}
          onClick={() => setIsMobileOpen(false)}
        />
        <Sidebar toggleSidebar={toggleSidebar} />
        <main className="container">
          <Outlet /> {/* changes depending on the route */}
        </main>
      </div>
      <UpdateBanner />
      {showChangelog && <WhatsNewModal onClose={closeChangelog} />}
      <NotificationToasts />
    </NotificationProvider>
  );
}
